// F-148 Reviewed Stock Receiving — service layer (design §2.2). Injected client; every function
// throws `StockReceivingError`. SQL raises ERRCODE 'P0480' with MESSAGE = '<F148_CODE>' and
// DETAIL = <json>. Request payloads are explicit snake_case literals built from typed values
// (never spreads of parsed CSV objects); every line literal carries `accepted_auto`.

import type { SupabaseClient } from '../supabase/client';
import type {
  CorrectReceiptLineInput,
  CorrectReceiptLineResult,
  CorrectionReason,
  CreateReceivingDraftInput,
  DiscardReceivingDraftInput,
  ImportReceivingRowsInput,
  ImportReceivingRowsResult,
  ListReceivingInput,
  Page,
  PostReceivingInput,
  PostReceivingResult,
  ReceivingAccess,
  ReceivingDraft,
  ReceivingDraftHeaderInput,
  ReceivingDraftIssueCode,
  ReceivingDraftLine,
  ReceivingDraftLineInput,
  ReceivingDraftStatus,
  ReceivingIdentifiers,
  ReceivingIssue,
  ReceivingLineIssueCode,
  ReceivingLineSource,
  ReceivingListItem,
  ReceivingMatchMethod,
  ReceivingPreview,
  ReceivingReferenceOrigin,
  ReceivingReferenceView,
  ReceivingSupplier,
  ReceivingTotals,
  ReceivingUnitKind,
  ReceivingVariantSnapshot,
  SaveReceivingDraftInput,
  SaveReceivingDraftResult,
  SearchReceivingVariantsInput,
  SearchReceivingVariantsResult,
  StockReceipt,
  StockReceiptCorrection,
  StockReceiptLine,
  StockReceivingErrorCode,
  StockReceivingErrorDetails,
} from '../types/stock-receiving';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const ERROR_CODES: ReadonlySet<string> = new Set<StockReceivingErrorCode>([
  'F148_NOT_FOUND',
  'F148_DISABLED',
  'F148_VALIDATION',
  'F148_DRAFT_EXISTS',
  'F148_DRAFT_VERSION_CONFLICT',
  'F148_DRAFT_NOT_OPEN',
  'F148_REVIEW_STALE',
  'F148_DRAFT_INVALID',
  'F148_REFERENCE_ALREADY_RECEIVED',
  'F148_IDEMPOTENCY_KEY_REUSED',
  'F148_VARIANT_INELIGIBLE',
  'F148_INVENTORY_LIMIT',
  'F148_STOCK_BUSY',
  'F148_CORRECTION_EXCEEDS_RECEIPT',
  'F148_CORRECTION_EXCEEDS_AVAILABLE',
  'F148_UNKNOWN',
]);

export class StockReceivingError extends Error {
  readonly code: StockReceivingErrorCode;
  readonly details: StockReceivingErrorDetails;
  /** true only for F148_STOCK_BUSY and transport failures (fetch error, timeout, bodiless 5xx). */
  readonly retryable: boolean;

  constructor(
    code: StockReceivingErrorCode,
    details: StockReceivingErrorDetails = {},
    retryable = false,
  ) {
    super(code);
    this.name = 'StockReceivingError';
    this.code = code;
    this.details = details;
    this.retryable = retryable;
  }
}

export function isStockReceivingError(value: unknown): value is StockReceivingError {
  return value instanceof StockReceivingError;
}

function isStockReceivingErrorCode(value: unknown): value is StockReceivingErrorCode {
  return typeof value === 'string' && ERROR_CODES.has(value);
}

function parseDetails(raw: unknown): StockReceivingErrorDetails {
  if (raw && typeof raw === 'object' && !Array.isArray(raw))
    return raw as StockReceivingErrorDetails;
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as StockReceivingErrorDetails;
      }
    } catch {
      // DETAIL was not JSON; fall through.
    }
  }
  return {};
}

/**
 * HTTP statuses that mean "the request may not have reached or completed on the server", so a
 * bounded same-key retry is safe. `0` is postgrest-js's status for a failed/aborted fetch;
 * 408/429 are the transient 4xx codes; every 5xx counts as a gateway/transport failure.
 */
function isTransientHttpStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

/**
 * Fetch/abort/gateway error text. postgrest-js wraps the fetch rejection as
 * `"<ErrorName>: <message>"` (`TypeError: Failed to fetch` in Chromium, `TypeError: Load failed`
 * in Safari, `TypeError: fetch failed` in Node, `AbortError: …` on abort).
 */
const TRANSPORT_MESSAGE =
  /^(TypeError|FetchError|AbortError|AuthRetryableFetchError)\b|fetch|network|timed? ?out|load failed|abort|socket|ECONN|ENOTFOUND|EAI_AGAIN/i;

/**
 * Classifies a code-less error (no SQLSTATE / PostgREST code) as a transport failure.
 * Never called for errors that carry a code — those are definitive server verdicts.
 * - Thrown `TypeError` / `AbortError` / `AuthRetryableFetchError` → transport.
 * - A numeric HTTP status decides when present: `0`, 408, 429 and 5xx (including bodiless or
 *   HTML gateway bodies) → transport; any other 4xx → definitive, regardless of message text.
 * - Without a status, fall back to the wrapped fetch/network message shape.
 */
function isTransportFailure(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const maybe = err as { name?: unknown; message?: unknown; status?: unknown };
  if (maybe?.name === 'AuthRetryableFetchError' || maybe?.name === 'AbortError') return true;
  if (typeof maybe?.status === 'number') return isTransientHttpStatus(maybe.status);
  return typeof maybe?.message === 'string' && TRANSPORT_MESSAGE.test(maybe.message);
}

/**
 * Maps any thrown value to `StockReceivingError` (§2.2, §9.2).
 * - `P0480` + `F148_*` message → that code, details from DETAIL json.
 * - `57014` (query_canceled; mapped client-side only, SEC-r2 NB1) → retryable
 *   `F148_STOCK_BUSY {cause:'timeout'}`.
 * - Any other non-empty code (SQLSTATE or PostgREST `PGRSTxxx`) → definitive
 *   `F148_UNKNOWN {sqlstate}`, never retried.
 * - No code (postgrest-js sends `code: ''` for fetch failures/aborts and no code at all for
 *   non-JSON HTTP error bodies) + transport signal → retryable `F148_UNKNOWN {cause:'transport'}`.
 * - Anything else → `F148_UNKNOWN {}` (plus `{status}` when an HTTP status is known).
 * No server/browser message text is ever copied into the mapped error.
 */
export function toStockReceivingError(err: unknown): StockReceivingError {
  if (isStockReceivingError(err)) return err;
  const maybe = err as {
    code?: unknown;
    message?: unknown;
    details?: unknown;
    hint?: unknown;
    status?: unknown;
  };
  // postgrest-js reports transport failures with `code: ''`; treat empty/blank as "no code".
  const code = typeof maybe?.code === 'string' && maybe.code.trim() !== '' ? maybe.code : null;

  if (code === '57014') {
    return new StockReceivingError('F148_STOCK_BUSY', { cause: 'timeout' }, true);
  }
  if (code === 'P0480' && isStockReceivingErrorCode(maybe.message)) {
    const details = parseDetails(maybe.details);
    return new StockReceivingError(maybe.message, details, maybe.message === 'F148_STOCK_BUSY');
  }
  if (code === 'P0480') {
    return new StockReceivingError('F148_UNKNOWN', { sqlstate: 'P0480' });
  }
  if (code !== null) {
    return new StockReceivingError('F148_UNKNOWN', { sqlstate: code });
  }
  if (isTransportFailure(err)) {
    return new StockReceivingError('F148_UNKNOWN', { cause: 'transport' }, true);
  }
  return new StockReceivingError(
    'F148_UNKNOWN',
    typeof maybe?.status === 'number' ? { status: maybe.status } : {},
  );
}

// ---------------------------------------------------------------------------
// Response narrowing (no casts that conceal contract defects)
// ---------------------------------------------------------------------------

type JsonRecord = Record<string, unknown>;

function malformed(path: string): StockReceivingError {
  return new StockReceivingError('F148_UNKNOWN', { cause: 'malformed_response', path });
}

function asRecord(value: unknown, path: string): JsonRecord {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as JsonRecord;
  throw malformed(path);
}

function asArray(value: unknown, path: string): unknown[] {
  if (Array.isArray(value)) return value;
  throw malformed(path);
}

function asString(value: unknown, path: string): string {
  if (typeof value === 'string') return value;
  throw malformed(path);
}

function asStringOrNull(value: unknown, path: string): string | null {
  if (value === null || value === undefined) return null;
  return asString(value, path);
}

function asInteger(value: unknown, path: string): number {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  throw malformed(path);
}

function asIntegerOrNull(value: unknown, path: string): number | null {
  if (value === null || value === undefined) return null;
  return asInteger(value, path);
}

function asBoolean(value: unknown, path: string): boolean {
  if (typeof value === 'boolean') return value;
  throw malformed(path);
}

function asEnum<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value === 'string' && (allowed as readonly string[]).includes(value))
    return value as T;
  throw malformed(path);
}

function asEnumOrNull<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T | null {
  if (value === null || value === undefined) return null;
  return asEnum(value, allowed, path);
}

const UNIT_KINDS: readonly ReceivingUnitKind[] = ['unit', 'pack'];
const LINE_SOURCES: readonly ReceivingLineSource[] = ['manual', 'csv', 'scan'];
const MATCH_METHODS: readonly ReceivingMatchMethod[] = ['barcode', 'variant_ref', 'manual'];
const DRAFT_STATUSES: readonly ReceivingDraftStatus[] = ['open', 'posted', 'discarded'];
const REFERENCE_ORIGINS: readonly ReceivingReferenceOrigin[] = ['merchant', 'system'];
const SEVERITIES = ['blocking', 'warning'] as const;
const CORRECTION_REASONS: readonly CorrectionReason[] = [
  'accepted_count_error',
  'damage_found_after_posting',
  'wrong_variant_received',
  'other',
];
const RECLASSIFY = ['missing', 'damaged'] as const;
const LINE_ISSUE_CODES: readonly ReceivingLineIssueCode[] = [
  'VARIANT_REQUIRED',
  'IDENTIFIER_UNKNOWN',
  'IDENTIFIER_AMBIGUOUS',
  'IDENTIFIER_CONFLICT',
  'VARIANT_INELIGIBLE',
  'PACK_CONFIRMATION_REQUIRED',
  'PACK_TOTAL_MISMATCH',
  'QUANTITY_MISSING',
  'QUANTITY_OUT_OF_RANGE',
  'QUANTITY_SUM_MISMATCH',
  'DUPLICATE_VARIANT',
  'INVENTORY_LIMIT_EXCEEDED',
];
const DRAFT_ISSUE_CODES: readonly ReceivingDraftIssueCode[] = [
  'NO_LINES',
  'TOO_MANY_LINES',
  'REFERENCE_ALREADY_RECEIVED',
  'DUPLICATE_CONTENT_UNACKNOWLEDGED',
  'RECEIVING_DISABLED',
  'SIMILAR_RECENT_RECEIPT',
];

function mapIssue<C extends string>(
  raw: unknown,
  allowed: readonly C[],
  path: string,
): ReceivingIssue<C> {
  const r = asRecord(raw, path);
  const issue: ReceivingIssue<C> = {
    code: asEnum(r.code, allowed, `${path}.code`),
    severity: asEnum(r.severity, SEVERITIES, `${path}.severity`),
  };
  if (r.lineNo !== null && r.lineNo !== undefined)
    issue.lineNo = asInteger(r.lineNo, `${path}.lineNo`);
  if (r.field !== null && r.field !== undefined) issue.field = asString(r.field, `${path}.field`);
  if (r.meta !== null && r.meta !== undefined) {
    const meta = asRecord(r.meta, `${path}.meta`);
    const out: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(meta)) {
      if (typeof value === 'string' || typeof value === 'number') out[key] = value;
      else if (value !== null && value !== undefined) out[key] = JSON.stringify(value);
    }
    issue.meta = out;
  }
  return issue;
}

function mapReferenceView(r: JsonRecord, path: string): ReceivingReferenceView {
  return {
    referenceOrigin: asEnum(r.referenceOrigin, REFERENCE_ORIGINS, `${path}.referenceOrigin`),
    shipmentReference: asStringOrNull(r.shipmentReference, `${path}.shipmentReference`),
    systemReference: asString(r.systemReference, `${path}.systemReference`),
    displayReference: asString(r.displayReference, `${path}.displayReference`),
  };
}

function mapSnapshot(raw: unknown, path: string): ReceivingVariantSnapshot {
  const r = asRecord(raw, path);
  return {
    variantId: asString(r.variantId, `${path}.variantId`),
    productId: asString(r.productId, `${path}.productId`),
    productName: asString(r.productName, `${path}.productName`),
    optionSummary: asString(r.optionSummary, `${path}.optionSummary`),
    barcode: asStringOrNull(r.barcode, `${path}.barcode`),
    imagePath: asStringOrNull(r.imagePath, `${path}.imagePath`),
    inventory: asInteger(r.inventory, `${path}.inventory`),
    active: asBoolean(r.active, `${path}.active`),
    eligible: asBoolean(r.eligible, `${path}.eligible`),
  };
}

function mapSnapshotOrNull(raw: unknown, path: string): ReceivingVariantSnapshot | null {
  if (raw === null || raw === undefined) return null;
  return mapSnapshot(raw, path);
}

function mapIdentifiers(raw: unknown, path: string): ReceivingIdentifiers {
  const r = asRecord(raw, path);
  return {
    barcode: asStringOrNull(r.barcode, `${path}.barcode`),
    variantRef: asStringOrNull(r.variantRef, `${path}.variantRef`),
    supplierCode: asStringOrNull(r.supplierCode, `${path}.supplierCode`),
    supplierItemName: asStringOrNull(r.supplierItemName, `${path}.supplierItemName`),
  };
}

function mapDraftLine(raw: unknown, path: string): ReceivingDraftLine {
  const r = asRecord(raw, path);
  const candidates = asRecord(r.candidates, `${path}.candidates`);
  return {
    lineNo: asInteger(r.lineNo, `${path}.lineNo`),
    clientLineId: asString(r.clientLineId, `${path}.clientLineId`),
    source: asEnum(r.source, LINE_SOURCES, `${path}.source`),
    sourceRowNumber: asIntegerOrNull(r.sourceRowNumber, `${path}.sourceRowNumber`),
    identifiers: mapIdentifiers(r.identifiers, `${path}.identifiers`),
    variantId: asStringOrNull(r.variantId, `${path}.variantId`),
    matchMethod: asEnumOrNull(r.matchMethod, MATCH_METHODS, `${path}.matchMethod`),
    unitKind: asEnum(r.unitKind, UNIT_KINDS, `${path}.unitKind`),
    packsExpected: asIntegerOrNull(r.packsExpected, `${path}.packsExpected`),
    unitsPerPack: asIntegerOrNull(r.unitsPerPack, `${path}.unitsPerPack`),
    unitsPerPackConfirmed: asIntegerOrNull(
      r.unitsPerPackConfirmed,
      `${path}.unitsPerPackConfirmed`,
    ),
    expectedUnits: asIntegerOrNull(r.expectedUnits, `${path}.expectedUnits`),
    acceptedUnits: asIntegerOrNull(r.acceptedUnits, `${path}.acceptedUnits`),
    damagedUnits: asIntegerOrNull(r.damagedUnits, `${path}.damagedUnits`),
    missingUnits: asIntegerOrNull(r.missingUnits, `${path}.missingUnits`),
    note: asStringOrNull(r.note, `${path}.note`),
    acceptedAuto: asBoolean(r.acceptedAuto, `${path}.acceptedAuto`),
    variant: mapSnapshotOrNull(r.variant, `${path}.variant`),
    candidates: {
      byBarcode: asArray(candidates.byBarcode, `${path}.candidates.byBarcode`).map((c, i) =>
        mapSnapshot(c, `${path}.candidates.byBarcode[${i}]`),
      ),
      byVariantRef: mapSnapshotOrNull(candidates.byVariantRef, `${path}.candidates.byVariantRef`),
    },
    issues: asArray(r.issues, `${path}.issues`).map((issue, i) =>
      mapIssue(issue, LINE_ISSUE_CODES, `${path}.issues[${i}]`),
    ),
  };
}

function mapSupplier(raw: unknown, path: string): ReceivingSupplier {
  const r = asRecord(raw, path);
  return { id: asString(r.id, `${path}.id`), name: asString(r.name, `${path}.name`) };
}

function mapTotals(raw: unknown, path: string): ReceivingTotals {
  const r = asRecord(raw, path);
  return {
    lines: asInteger(r.lines, `${path}.lines`),
    expected: asInteger(r.expected, `${path}.expected`),
    accepted: asInteger(r.accepted, `${path}.accepted`),
    damaged: asInteger(r.damaged, `${path}.damaged`),
    missing: asInteger(r.missing, `${path}.missing`),
  };
}

function mapDraft(raw: unknown): ReceivingDraft {
  const r = asRecord(raw, 'draft');
  return {
    ...mapReferenceView(r, 'draft'),
    id: asString(r.id, 'draft.id'),
    storeId: asString(r.storeId, 'draft.storeId'),
    status: asEnum(r.status, DRAFT_STATUSES, 'draft.status'),
    version: asInteger(r.version, 'draft.version'),
    supplier: mapSupplier(r.supplier, 'draft.supplier'),
    linkedReceiptId: asStringOrNull(r.linkedReceiptId, 'draft.linkedReceiptId'),
    note: asStringOrNull(r.note, 'draft.note'),
    createdAt: asString(r.createdAt, 'draft.createdAt'),
    updatedAt: asString(r.updatedAt, 'draft.updatedAt'),
    postedReceiptId: asStringOrNull(r.postedReceiptId, 'draft.postedReceiptId'),
    lines: asArray(r.lines, 'draft.lines').map((line, i) =>
      mapDraftLine(line, `draft.lines[${i}]`),
    ),
    issues: asArray(r.issues, 'draft.issues').map((issue, i) =>
      mapIssue(issue, DRAFT_ISSUE_CODES, `draft.issues[${i}]`),
    ),
  };
}

function mapPreview(raw: unknown): ReceivingPreview {
  const r = asRecord(raw, 'preview');
  const allowed: ReadonlyArray<ReceivingDraftIssueCode | ReceivingLineIssueCode> = [
    ...DRAFT_ISSUE_CODES,
    ...LINE_ISSUE_CODES,
  ];
  return {
    draftId: asString(r.draftId, 'preview.draftId'),
    version: asInteger(r.version, 'preview.version'),
    reviewToken: asString(r.reviewToken, 'preview.reviewToken'),
    ready: asBoolean(r.ready, 'preview.ready'),
    issues: asArray(r.issues, 'preview.issues').map((issue, i) =>
      mapIssue(issue, allowed, `preview.issues[${i}]`),
    ),
    deltas: asArray(r.deltas, 'preview.deltas').map((delta, i) => {
      const d = asRecord(delta, `preview.deltas[${i}]`);
      return {
        variantId: asString(d.variantId, `preview.deltas[${i}].variantId`),
        productName: asString(d.productName, `preview.deltas[${i}].productName`),
        optionSummary: asString(d.optionSummary, `preview.deltas[${i}].optionSummary`),
        inventoryNow: asInteger(d.inventoryNow, `preview.deltas[${i}].inventoryNow`),
        acceptedUnits: asInteger(d.acceptedUnits, `preview.deltas[${i}].acceptedUnits`),
      };
    }),
    totals: mapTotals(r.totals, 'preview.totals'),
  };
}

function mapCorrection(raw: unknown, path: string): StockReceiptCorrection {
  const r = asRecord(raw, path);
  return {
    id: asString(r.id, `${path}.id`),
    receiptLineId: asString(r.receiptLineId, `${path}.receiptLineId`),
    units: asInteger(r.units, `${path}.units`),
    reason: asEnum(r.reason, CORRECTION_REASONS, `${path}.reason`),
    reclassifyAs: asEnum(r.reclassifyAs, RECLASSIFY, `${path}.reclassifyAs`),
    note: asStringOrNull(r.note, `${path}.note`),
    inventoryBefore: asInteger(r.inventoryBefore, `${path}.inventoryBefore`),
    inventoryAfter: asInteger(r.inventoryAfter, `${path}.inventoryAfter`),
    createdAt: asString(r.createdAt, `${path}.createdAt`),
    createdByEmail: asString(r.createdByEmail, `${path}.createdByEmail`),
  };
}

function mapReceiptLine(raw: unknown, path: string): StockReceiptLine {
  const r = asRecord(raw, path);
  return {
    id: asString(r.id, `${path}.id`),
    lineNo: asInteger(r.lineNo, `${path}.lineNo`),
    variantId: asString(r.variantId, `${path}.variantId`),
    productId: asString(r.productId, `${path}.productId`),
    productNameSnapshot: asString(r.productNameSnapshot, `${path}.productNameSnapshot`),
    optionSummarySnapshot: asString(r.optionSummarySnapshot, `${path}.optionSummarySnapshot`),
    barcodeSnapshot: asStringOrNull(r.barcodeSnapshot, `${path}.barcodeSnapshot`),
    identifiers: mapIdentifiers(r.identifiers, `${path}.identifiers`),
    unitKind: asEnum(r.unitKind, UNIT_KINDS, `${path}.unitKind`),
    packsExpected: asIntegerOrNull(r.packsExpected, `${path}.packsExpected`),
    unitsPerPack: asIntegerOrNull(r.unitsPerPack, `${path}.unitsPerPack`),
    expectedUnits: asInteger(r.expectedUnits, `${path}.expectedUnits`),
    acceptedUnits: asInteger(r.acceptedUnits, `${path}.acceptedUnits`),
    damagedUnits: asInteger(r.damagedUnits, `${path}.damagedUnits`),
    missingUnits: asInteger(r.missingUnits, `${path}.missingUnits`),
    inventoryBefore: asIntegerOrNull(r.inventoryBefore, `${path}.inventoryBefore`),
    inventoryAfter: asIntegerOrNull(r.inventoryAfter, `${path}.inventoryAfter`),
    correctedUnits: asInteger(r.correctedUnits, `${path}.correctedUnits`),
    correctableUnits: asInteger(r.correctableUnits, `${path}.correctableUnits`),
    note: asStringOrNull(r.note, `${path}.note`),
  };
}

function mapReceipt(raw: unknown): StockReceipt {
  const r = asRecord(raw, 'receipt');
  const supplier = asRecord(r.supplier, 'receipt.supplier');
  return {
    ...mapReferenceView(r, 'receipt'),
    id: asString(r.id, 'receipt.id'),
    storeId: asString(r.storeId, 'receipt.storeId'),
    draftId: asString(r.draftId, 'receipt.draftId'),
    supplier: {
      id: asString(supplier.id, 'receipt.supplier.id'),
      nameSnapshot: asString(supplier.nameSnapshot, 'receipt.supplier.nameSnapshot'),
    },
    linkedReceiptId: asStringOrNull(r.linkedReceiptId, 'receipt.linkedReceiptId'),
    postedAt: asString(r.postedAt, 'receipt.postedAt'),
    postedByEmail: asString(r.postedByEmail, 'receipt.postedByEmail'),
    totals: mapTotals(r.totals, 'receipt.totals'),
    lines: asArray(r.lines, 'receipt.lines').map((line, i) =>
      mapReceiptLine(line, `receipt.lines[${i}]`),
    ),
    corrections: asArray(r.corrections, 'receipt.corrections').map((c, i) =>
      mapCorrection(c, `receipt.corrections[${i}]`),
    ),
  };
}

function mapListItem(raw: unknown, path: string): ReceivingListItem {
  const r = asRecord(raw, path);
  return {
    ...mapReferenceView(r, path),
    kind: asEnum(r.kind, ['draft', 'receipt'] as const, `${path}.kind`),
    id: asString(r.id, `${path}.id`),
    supplierName: asString(r.supplierName, `${path}.supplierName`),
    lineCount: asInteger(r.lineCount, `${path}.lineCount`),
    acceptedUnits: asIntegerOrNull(r.acceptedUnits, `${path}.acceptedUnits`),
    updatedAt: asString(r.updatedAt, `${path}.updatedAt`),
  };
}

// ---------------------------------------------------------------------------
// Request payloads (explicit snake_case literals)
// ---------------------------------------------------------------------------
// Declared as object-literal type aliases (not interfaces) so they structurally satisfy the
// generated `Json` arg type of the `jsonb` RPC parameters: TS gives type-literal aliases an
// implicit index signature, while interfaces get none. Excess-property checks on the literal
// builders below are unaffected.

type SupplierPayload = { kind: 'existing'; supplier_id: string } | { kind: 'new'; name: string };

type HeaderPayload = {
  supplier: SupplierPayload;
  shipment_reference: string | null;
  linked_receipt_id: string | null;
  note: string | null;
};

type LinePayload = {
  client_line_id: string;
  source: ReceivingLineSource;
  source_row_number: number | null;
  barcode: string | null;
  variant_ref: string | null;
  supplier_code: string | null;
  supplier_item_name: string | null;
  variant_id: string | null;
  match_method: ReceivingMatchMethod | null;
  unit_kind: ReceivingUnitKind;
  packs_expected: number | null;
  units_per_pack: number | null;
  units_per_pack_confirmed: number | null;
  expected_units: number | null;
  accepted_units: number | null;
  damaged_units: number | null;
  missing_units: number | null;
  note: string | null;
  accepted_auto: boolean;
};

type ImportRowPayload = {
  source_row_number: number;
  barcode: string | null;
  variant_ref: string | null;
  supplier_code: string | null;
  supplier_item_name: string | null;
  unit_kind: ReceivingUnitKind;
  packs_expected: number | null;
  units_per_pack: number | null;
  expected_units: number;
  accepted_units: number;
  damaged_units: number;
  missing_units: number;
  note: string | null;
  accepted_auto: boolean;
};

/**
 * Opaque paging cursor arg. `p_cursor text DEFAULT NULL` in SQL; a null cursor means "first page",
 * so the parameter is omitted entirely (never sent as '' or any other real cursor value).
 */
function cursorArg(cursor: string | null): { p_cursor: string } | Record<never, never> {
  return cursor === null ? {} : { p_cursor: cursor };
}

function buildHeaderPayload(header: ReceivingDraftHeaderInput): HeaderPayload {
  const supplier: SupplierPayload =
    header.supplier.kind === 'existing'
      ? { kind: 'existing', supplier_id: header.supplier.supplierId }
      : { kind: 'new', name: header.supplier.name };
  return {
    supplier,
    shipment_reference: header.shipmentReference,
    linked_receipt_id: header.linkedReceiptId,
    note: header.note,
  };
}

function buildLinePayload(line: ReceivingDraftLineInput): LinePayload {
  if (typeof line.acceptedAuto !== 'boolean') {
    throw new StockReceivingError('F148_VALIDATION', { field: 'acceptedAuto', reason: 'missing' });
  }
  return {
    client_line_id: line.clientLineId,
    source: line.source,
    source_row_number: line.sourceRowNumber,
    barcode: line.identifiers.barcode,
    variant_ref: line.identifiers.variantRef,
    supplier_code: line.identifiers.supplierCode,
    supplier_item_name: line.identifiers.supplierItemName,
    variant_id: line.variantId,
    match_method: line.matchMethod,
    unit_kind: line.unitKind,
    packs_expected: line.packsExpected,
    units_per_pack: line.unitsPerPack,
    units_per_pack_confirmed: line.unitsPerPackConfirmed,
    expected_units: line.expectedUnits,
    accepted_units: line.acceptedUnits,
    damaged_units: line.damagedUnits,
    missing_units: line.missingUnits,
    note: line.note,
    accepted_auto: line.acceptedAuto,
  };
}

function buildImportRowPayload(row: ImportReceivingRowsInput['rows'][number]): ImportRowPayload {
  if (typeof row.acceptedAuto !== 'boolean') {
    throw new StockReceivingError('F148_VALIDATION', { field: 'acceptedAuto', reason: 'missing' });
  }
  return {
    source_row_number: row.sourceRowNumber,
    barcode: row.identifiers.barcode,
    variant_ref: row.identifiers.variantRef,
    supplier_code: row.identifiers.supplierCode,
    supplier_item_name: row.identifiers.supplierItemName,
    unit_kind: row.unitKind,
    packs_expected: row.packsExpected,
    units_per_pack: row.unitsPerPack,
    expected_units: row.expectedUnits,
    accepted_units: row.acceptedUnits,
    damaged_units: row.damagedUnits,
    missing_units: row.missingUnits,
    note: row.note,
    accepted_auto: row.acceptedAuto,
  };
}

/**
 * Copies the envelope's HTTP `status` onto a plain-object error that has none of its own, so the
 * mapper can tell a fetch failure (`status: 0`) or bodiless 5xx from a definitive 4xx. Thrown
 * `Error` instances are left untouched (their prototype carries the classification).
 */
function withResponseStatus(error: unknown, status: unknown): unknown {
  if (typeof status !== 'number') return error;
  if (!error || typeof error !== 'object' || error instanceof Error) return error;
  const record = error as Record<string, unknown>;
  if (typeof record.status === 'number') return error;
  return { ...record, status };
}

async function callRpc<T>(
  request: PromiseLike<{ data: unknown; error: unknown; status?: unknown }>,
  map: (data: unknown) => T,
): Promise<T> {
  let response: { data: unknown; error: unknown; status?: unknown };
  try {
    response = await request;
  } catch (err) {
    throw toStockReceivingError(err);
  }
  if (response.error)
    throw toStockReceivingError(withResponseStatus(response.error, response.status));
  return map(response.data);
}

// ---------------------------------------------------------------------------
// Public service functions (§2.2)
// ---------------------------------------------------------------------------

export async function getReceivingAccess(
  client: SupabaseClient,
  storeId: string,
): Promise<ReceivingAccess> {
  return callRpc(client.rpc('receiving_get_access', { p_store_id: storeId }), (data) => {
    const r = asRecord(data, 'access');
    return {
      enabled: asBoolean(r.enabled, 'access.enabled'),
      canManage: asBoolean(r.canManage, 'access.canManage'),
    };
  });
}

export async function listReceivingSuppliers(
  client: SupabaseClient,
  storeId: string,
  search: string,
): Promise<ReceivingSupplier[]> {
  return callRpc(
    client.rpc('receiving_list_suppliers', { p_store_id: storeId, p_query: search }),
    (data) => asArray(data, 'suppliers').map((s, i) => mapSupplier(s, `suppliers[${i}]`)),
  );
}

export async function createReceivingDraft(
  client: SupabaseClient,
  input: CreateReceivingDraftInput,
): Promise<ReceivingDraft> {
  return callRpc(
    client.rpc('receiving_create_draft', {
      p_store_id: input.storeId,
      p_client_request_id: input.clientRequestId,
      p_header: buildHeaderPayload(input.header),
    }),
    mapDraft,
  );
}

export async function getReceivingDraft(
  client: SupabaseClient,
  draftId: string,
): Promise<ReceivingDraft> {
  return callRpc(client.rpc('receiving_get_draft', { p_draft_id: draftId }), mapDraft);
}

export async function saveReceivingDraft(
  client: SupabaseClient,
  input: SaveReceivingDraftInput,
): Promise<SaveReceivingDraftResult> {
  return callRpc(
    client.rpc('receiving_save_draft', {
      p_draft_id: input.draftId,
      p_expected_version: input.expectedVersion,
      p_save_token: input.saveToken,
      p_header: buildHeaderPayload(input.header),
      p_lines: input.lines.map(buildLinePayload),
    }),
    (data) => {
      const r = asRecord(data, 'save');
      return {
        version: asInteger(r.version, 'save.version'),
        replayed: asBoolean(r.replayed, 'save.replayed'),
      };
    },
  );
}

export async function importReceivingRows(
  client: SupabaseClient,
  input: ImportReceivingRowsInput,
): Promise<ImportReceivingRowsResult> {
  return callRpc(
    client.rpc('receiving_import_rows', {
      p_draft_id: input.draftId,
      p_expected_version: input.expectedVersion,
      p_save_token: input.saveToken,
      p_rows: input.rows.map(buildImportRowPayload),
      p_file_meta: {
        file_name: input.fileMeta.fileName,
        byte_size: input.fileMeta.byteSize,
        row_count: input.fileMeta.rowCount,
      },
      p_acknowledge_duplicate: input.acknowledgeDuplicate,
    }),
    (data) => {
      const r = asRecord(data, 'import');
      const outcome = asEnum(
        r.outcome,
        ['imported', 'duplicate_warning'] as const,
        'import.outcome',
      );
      if (outcome === 'imported') {
        return {
          outcome,
          version: asInteger(r.version, 'import.version'),
          replayed: asBoolean(r.replayed, 'import.replayed'),
        };
      }
      return {
        outcome,
        matches: asArray(r.matches, 'import.matches').map((m, i) => {
          const match = asRecord(m, `import.matches[${i}]`);
          return {
            kind: asEnum(match.kind, ['draft', 'receipt'] as const, `import.matches[${i}].kind`),
            id: asString(match.id, `import.matches[${i}].id`),
            displayReference: asString(
              match.displayReference,
              `import.matches[${i}].displayReference`,
            ),
          };
        }),
      };
    },
  );
}

export async function discardReceivingDraft(
  client: SupabaseClient,
  input: DiscardReceivingDraftInput,
): Promise<void> {
  await callRpc(
    client.rpc('receiving_discard_draft', {
      p_draft_id: input.draftId,
      p_expected_version: input.expectedVersion,
    }),
    () => undefined,
  );
}

export async function searchReceivingVariants(
  client: SupabaseClient,
  input: SearchReceivingVariantsInput,
  opts?: { signal?: AbortSignal },
): Promise<SearchReceivingVariantsResult> {
  let request = client.rpc('receiving_search_variants', {
    p_store_id: input.storeId,
    p_query: input.query,
    ...cursorArg(input.cursor),
    p_limit: 25,
  });
  if (opts?.signal) request = request.abortSignal(opts.signal);
  return callRpc(request, (data) => {
    const r = asRecord(data, 'search');
    return {
      items: asArray(r.items, 'search.items').map((s, i) => mapSnapshot(s, `search.items[${i}]`)),
      nextCursor: asStringOrNull(r.nextCursor, 'search.nextCursor'),
      exactBarcode: mapSnapshotOrNull(r.exactBarcode, 'search.exactBarcode'),
    };
  });
}

export async function previewReceivingPost(
  client: SupabaseClient,
  draftId: string,
): Promise<ReceivingPreview> {
  return callRpc(client.rpc('receiving_preview_post', { p_draft_id: draftId }), mapPreview);
}

export async function postReceivingDraft(
  client: SupabaseClient,
  input: PostReceivingInput,
): Promise<PostReceivingResult> {
  return callRpc(
    client.rpc('receiving_post_draft', {
      p_draft_id: input.draftId,
      p_expected_version: input.expectedVersion,
      p_review_token: input.reviewToken,
      p_idempotency_key: input.idempotencyKey,
    }),
    (data) => {
      const r = asRecord(data, 'post');
      return {
        outcome: asEnum(
          r.outcome,
          ['posted', 'replayed', 'already_posted'] as const,
          'post.outcome',
        ),
        receiptId: asString(r.receiptId, 'post.receiptId'),
      };
    },
  );
}

export async function listReceiving(
  client: SupabaseClient,
  input: ListReceivingInput,
): Promise<Page<ReceivingListItem>> {
  return callRpc(
    client.rpc('receiving_list', {
      p_store_id: input.storeId,
      p_kind: input.kind,
      ...cursorArg(input.cursor),
      p_limit: 25,
    }),
    (data) => {
      const r = asRecord(data, 'list');
      return {
        items: asArray(r.items, 'list.items').map((item, i) =>
          mapListItem(item, `list.items[${i}]`),
        ),
        nextCursor: asStringOrNull(r.nextCursor, 'list.nextCursor'),
      };
    },
  );
}

export async function getStockReceipt(
  client: SupabaseClient,
  receiptId: string,
): Promise<StockReceipt> {
  return callRpc(client.rpc('receiving_get_receipt', { p_receipt_id: receiptId }), mapReceipt);
}

export async function correctReceiptLine(
  client: SupabaseClient,
  input: CorrectReceiptLineInput,
): Promise<CorrectReceiptLineResult> {
  return callRpc(
    client.rpc('receiving_correct_line', {
      p_receipt_line_id: input.receiptLineId,
      p_units: input.units,
      p_reason: input.reason,
      p_reclassify_as: input.reclassifyAs,
      // SQL canonicalizes both null and empty text to a null note before hashing.
      p_note: input.note ?? '',
      p_idempotency_key: input.idempotencyKey,
    }),
    (data) => {
      const r = asRecord(data, 'correct');
      return {
        outcome: asEnum(r.outcome, ['applied', 'replayed'] as const, 'correct.outcome'),
        correction: mapCorrection(r.correction, 'correct.correction'),
      };
    },
  );
}

export const stockReceivingService = {
  getReceivingAccess,
  listReceivingSuppliers,
  createReceivingDraft,
  getReceivingDraft,
  saveReceivingDraft,
  importReceivingRows,
  discardReceivingDraft,
  searchReceivingVariants,
  previewReceivingPost,
  postReceivingDraft,
  listReceiving,
  getStockReceipt,
  correctReceiptLine,
} as const;

export type StockReceivingService = typeof stockReceivingService;
